import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Video, Users, Phone, BookOpen, Shield, ArrowRight,
  Zap, Wrench, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useEmblaCarousel from "embla-carousel-react";
import heroBg from "@/assets/hero-bg-new.jpg";
import brandonImg from "@/assets/brandon-turner.jpg";

// ── Data ──────────────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: BookOpen, title: "55 Video Lessons", desc: "Step-by-step foundations course covering every aspect of real estate investing." },
  { icon: Video, title: "Live Weekly Calls", desc: "Join millionaire investors every week for live deal reviews and Q&A." },
  { icon: Users, title: "Active Community", desc: "Connect with hundreds of like-minded investors on the same journey." },
  { icon: Phone, title: "1-on-1 Mentor Calls", desc: "Get direct access to an experienced investor for personalized guidance." },
];

const SOCIAL_PROOF = ["Entrepreneur", "Forbes", "Money", "BiggerPockets", "BetterLife"];

const TESTIMONIALS = [
  {
    name: "Kyle Frank",
    initials: "KF",
    result: "Seller-financed 5-plex — deal #2",
    quote: "When we joined this group, the only thing on our minds was getting ONE deal. Just one. That first deal set off a chain reaction we never saw coming. Last Thursday we closed on a SELLER-FINANCED 5-PLEX -- something we NEVER thought we'd be able to pull off this early.",
  },
  {
    name: "Audrey Li",
    initials: "AL",
    result: "First rental live — booked the same day",
    quote: "Huge shoutout to the First Deal team! The courses were incredibly helpful in getting us started, and the timely consultation made the offer and closing process so much smoother. Our listing just went live yesterday, and we got our first booking today!",
  },
  {
    name: "Tanya Zorov",
    initials: "TZ",
    result: "2 co-living homes + 12 tenants in one day",
    quote: "I became a two-property owner in one day. Two PadSplit homes in a solid Jacksonville neighborhood. 12 tenants in place with one more room to fill. The hardest part wasn't underwriting; it was choosing a strategy and location. I picked one, trusted my gut, and committed.",
  },
  {
    name: "Tyler Peery",
    initials: "TP",
    result: "Fix-and-flip with a partner — first deal",
    quote: "Closed on my first deal yesterday! This will be a fix and flip. JV with a friend of mine. Had our GC with the demo crew at 7:30am this morning starting demo and roof replacement!",
  },
  {
    name: "Brent M Bagen",
    initials: "BB",
    result: "First off-market deal, hard money loan",
    quote: "Closed today on my first BRRRR. A lot of firsts on this one -- first off market deal, first time filling out my own buy sell agreement, first hard money loan with rehab cost built-in. It should cash flow pretty well for my area.",
  },
  {
    name: "Fernando Villasol",
    initials: "FV",
    result: "2nd property under contract — short-term rental",
    quote: "Under contract and passed inspection on my second property since I joined. This one will be a short-term rental.",
  },
  {
    name: "Debi Windle",
    initials: "DW",
    result: "Senior care home in Palm Springs — first deal",
    quote: "Picked up the keys to my first investment property yesterday. 4B 2.5BA in greater Palm Springs. I'm opening a 4-6 bed residential care home for the elderly. Two bucket list items with two businesses in one location. Thanks are due to First Deal REI mentors!",
  },
  {
    name: "Allen Ramirez",
    initials: "AR",
    result: "First deal: duplex in Indianapolis",
    quote: "Hello FD Family! I am happy to share that I just closed on my 1st property today! It's a duplex in Indianapolis, IN. Thanks to Brandon Turner and all the FD mentors for the constant guidance! On to the next one!",
  },
  {
    name: "Becky Makous",
    initials: "BM",
    result: "9-bed co-living conversion — first deal",
    quote: "First deal signed!!! It's a 5 bed/3 bath that I'm going to renovate into a 9-10 bed/3 bath and put it on PadSplit to generate some gorgeous cashflow. Moving into uncharted territory with large sums of money -- but it's all very exciting.",
  },
  {
    name: "Jon Barnier",
    initials: "JB",
    result: "Vacation rental accepted — 30 days in",
    quote: "I joined First Deal on February 26th, and today on March 28th my offer on a SFH for a STR was accepted! It's on a beautiful river, 15 minutes from a major ski resort. We even got a 2pt seller buydown that dropped our rate and gave us a larger monthly cash flow buffer.",
  },
  {
    name: "Felix Perez",
    initials: "FP",
    result: "Long-distance deal closed, $1,100/mo rent",
    quote: "Joined First Deal in June. Committed 100% to finding a long-distance deal in Ohio while living in New York. Searched through hundreds of deals. Closed for $65,000 and officially signed leases for $1,100 a month. Thank you for all the help!",
  },
  {
    name: "Jered Goldwasser",
    initials: "JG",
    result: "4-unit building, negotiated $23k off — first deal",
    quote: "I closed on my first deal earlier this week! It's a 4-plex with all 2br 1ba units in Cincinnati, OH. Negotiated down from $350k asking to $327k plus $8.5k seller credits. Going to stick to my strategy and do a slow BRRRR as units become vacant.",
  },
];

const INCLUDED = [
  {
    icon: BookOpen,
    title: "Foundations Course",
    desc: "55 video lessons covering every major strategy, with templates and lifetime access.",
  },
  {
    icon: Phone,
    title: "1-on-1 Millionaire Mentor",
    desc: "Book a call with an investor who has done exactly what you are trying to do.",
  },
  {
    icon: Video,
    title: "Bottleneck Breakthrough Calls",
    desc: "Live weekly group coaching with millionaire investors. Bring your deals. Get answers.",
  },
  {
    icon: Users,
    title: "First Deal Community",
    desc: "An active private network of investors to share deals, find partners, and stay accountable.",
  },
  {
    icon: Wrench,
    title: "Tools and Calculators",
    desc: "Unlimited access to every calculator, the Market Finder, and professional deal reports.",
  },
  {
    icon: Zap,
    title: "5-Week Fast-Track",
    desc: "A structured sprint that gets you from zero to actively pursuing your first deal.",
  },
];


const ROADMAP = [
  { week: "Week 1", title: "Foundation", desc: "Set your investing identity and define exactly what your first deal looks like." },
  { week: "Week 2", title: "Decisions", desc: "Build your Crystal Clear Criteria so you never waste time on the wrong deal." },
  { week: "Week 3", title: "Deal Flow", desc: "Activate your pipeline and start generating real deal opportunities." },
  { week: "Week 4", title: "Debt and Dollars", desc: "Lock in your financing strategy and capital sources before you need them." },
  { week: "Week 5", title: "Disciplines", desc: "Install the habits and systems that keep you in motion for the next 12 months." },
];

// ── Testimonials Carousel ─────────────────────────────────────────────────────

const ROW_A = TESTIMONIALS.slice(0, 6);
const ROW_B = TESTIMONIALS.slice(6, 12);

const TestimonialCard = ({ name, initials, result, quote }: { name: string; initials: string; result: string; quote: string }) => (
  <div className="rounded-xl border border-border bg-card p-7 flex flex-col gap-5 shadow-card h-full">
    <div className="flex items-center gap-3">
      <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full gradient-gold text-white text-sm font-bold">
        {initials}
      </div>
      <div>
        <p className="text-base font-semibold text-foreground">{name}</p>
        <span className="inline-block mt-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-primary/10 text-primary">
          {result}
        </span>
      </div>
    </div>
    <p className="text-base text-muted-foreground leading-relaxed flex-1">
      "{quote}"
    </p>
  </div>
);

const TestimonialsCarousel = () => {
  const [rowARef, rowAApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [rowBRef, rowBApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => { rowAApi?.scrollPrev(); rowBApi?.scrollNext(); }, [rowAApi, rowBApi]);
  const scrollNext = useCallback(() => { rowAApi?.scrollNext(); rowBApi?.scrollPrev(); }, [rowAApi, rowBApi]);

  useEffect(() => {
    if (!rowAApi) return;
    const onSelect = () => setSelectedIndex(rowAApi.selectedScrollSnap());
    rowAApi.on("select", onSelect);
    onSelect();
    return () => { rowAApi.off("select", onSelect); };
  }, [rowAApi]);

  // Row A scrolls forward every 7s, Row B scrolls backward (opposite direction)
  useEffect(() => {
    if (!rowAApi) return;
    const t = setInterval(() => rowAApi.scrollNext(), 7000);
    return () => clearInterval(t);
  }, [rowAApi]);

  useEffect(() => {
    if (!rowBApi) return;
    const t = setInterval(() => rowBApi.scrollPrev(), 8500);
    return () => clearInterval(t);
  }, [rowBApi]);

  return (
    <section className="py-20 bg-white border-t border-border">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-12 text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary">Straight From the Community</p>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Real Wins. Real Members.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            These are actual posts from the First Deal community. Unedited. Unprompted.
          </p>
        </div>
      </div>

      {/* Row A */}
      <div className="overflow-hidden mb-6 mx-6" ref={rowARef}>
        <div className="flex gap-6">
          {ROW_A.map(({ name, initials, result, quote }, i) => (
            <div key={`a-${i}`} className="flex-none w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
              <TestimonialCard name={name} initials={initials} result={result} quote={quote} />
            </div>
          ))}
        </div>
      </div>

      {/* Row B — staggered */}
      <div className="overflow-hidden mx-6" ref={rowBRef}>
        <div className="flex gap-6">
          {ROW_B.map(({ name, initials, result, quote }, i) => (
            <div key={`b-${i}`} className="flex-none w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
              <TestimonialCard name={name} initials={initials} result={result} quote={quote} />
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mx-auto max-w-7xl px-4">
        <div className="mt-8 flex items-center justify-center gap-4">
          <button onClick={scrollPrev} className="h-10 w-10 rounded-full border border-border bg-white flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 shadow-sm transition-colors" aria-label="Previous">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {ROW_A.map((_, i) => (
              <button key={i} onClick={() => { rowAApi?.scrollTo(i); rowBApi?.scrollTo(ROW_B.length - 1 - i); }}
                className={`h-2 rounded-full transition-all duration-300 ${i === selectedIndex ? "w-6 gradient-gold" : "w-2 bg-border"}`}
                aria-label={`Go to slide ${i + 1}`} />
            ))}
          </div>
          <button onClick={scrollNext} className="h-10 w-10 rounded-full border border-border bg-white flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 shadow-sm transition-colors" aria-label="Next">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const Home = () => (
  <div>

    {/* ── Hero ── */}
    <section className="relative flex min-h-[85vh] items-center overflow-hidden">
      <img src={heroBg} alt="" className="absolute inset-0 h-full w-full object-cover object-[82%_20%] sm:object-center" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />
      <div className="relative z-10 w-full py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl text-white">
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-primary">Brandon Turner's 12-Month Mastermind</p>
            <h1 className="font-serif text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Buy Your First Deal in the Next 12 Months.{" "}
              <span className="relative inline-block">
                Guaranteed.
                <span className="absolute left-0 -bottom-1 h-[3px] w-full rounded-full bg-primary" />
              </span>
            </h1>
            <p className="mt-6 text-lg text-white/80">
              A proven 12-month program that gives you the education, mentorship, and community to close your first real estate deal. Backed by a full guarantee.
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button asChild size="lg" className="gradient-gold shadow-gold px-10 text-base text-white">
                <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">Apply to First Deal <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/50 bg-white/10 text-white hover:bg-white/20 hover:border-white/70">
                <Link to="/tools">Try Free Calculators</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Social Proof Bar ── */}
    <section className="border-b border-border bg-card py-8">
      <div className="mx-auto max-w-5xl px-4 text-center">
        <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Brandon Has Been Featured In</p>
        <div className="flex flex-wrap items-center justify-center gap-8">
          {SOCIAL_PROOF.map((name) => (
            <span key={name} className="text-lg font-semibold text-muted-foreground/50">{name}</span>
          ))}
        </div>
      </div>
    </section>

    {/* ── Program Overview ── */}
    <section className="py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-12 text-center">
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Everything You Need to Close Your First Deal</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            First Deal combines world-class education, expert mentorship, and a driven community into one complete system.
          </p>
        </div>

        <div className="mx-auto mb-16 max-w-3xl overflow-hidden rounded-xl shadow-card">
          <wistia-player media-id="khxs3kej31" aspect="1.7777777777777777"></wistia-player>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <Card key={title} className="shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
              <CardContent className="p-6 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-serif text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* ── Testimonials ── */}
    <TestimonialsCarousel />

    {/* ── Guarantee ── */}
    <section className="py-20 bg-foreground text-background">
      <div className="mx-auto max-w-4xl px-4 text-center">
        <Shield className="mx-auto mb-4 h-14 w-14 text-primary" />
        <h2 className="font-serif text-3xl font-bold sm:text-4xl text-background">The First Deal Guarantee</h2>
        <p className="mx-auto mt-4 max-w-2xl text-background/60">
          Complete the program, do the work, and if you don't close a deal within 12 months, we'll work with you for free until you do. That's how confident we are in this system.
        </p>
        <Button asChild size="lg" className="mt-8 gradient-gold shadow-gold text-white">
          <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">Apply to First Deal</a>
        </Button>
      </div>
    </section>

    {/* ── About Brandon ── */}
    <section className="bg-muted/40 py-20 border-t border-border">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl shadow-card">
            <img src={brandonImg} alt="Brandon Turner" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-primary">Meet Your Guide</p>
            <h2 className="font-serif text-3xl font-bold sm:text-4xl">Brandon Turner</h2>
            <div className="mt-6 space-y-4 text-muted-foreground">
              <p>Brandon Turner is a real estate investor, author, and educator who has helped thousands of people start and scale their investing journey. His portfolio spans 14,000+ units across multiple strategies and markets.</p>
              <p>As the former host of the BiggerPockets Podcast, the world's number one real estate investing podcast, Brandon interviewed over 500 investors and distilled their strategies into a repeatable system anyone can follow.</p>
              <p>Today, Brandon personally leads First Deal, bringing his investors, his playbook, and his network directly to new investors who are serious about closing their first deal.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── What's Included ── */}
    <section className="py-20 border-t border-border">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-4 text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary">The Complete System</p>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Everything Inside First Deal</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            One membership that gives you every tool, every mentor, and every system you need to close your first deal.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map(({ icon: Icon, title, desc }) => (
            <Card
              key={title}
              className="shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200"
            >
              <CardContent className="p-7">
                <div className="mb-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <h3 className="font-serif text-lg font-bold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button asChild size="lg" className="gradient-gold shadow-gold text-white px-10">
            <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">
              Get Everything Above <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>

    {/* ── 5-Week Fast-Track Roadmap ── */}
    <section className="py-20">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-12 text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary">Your First 5 Weeks</p>
          <h2 className="font-serif text-3xl font-bold sm:text-4xl">Deal-Ready in 35 Days</h2>
          <p className="mt-4 text-muted-foreground">
            The Fast-Track is a structured 5-week sprint that takes you from where you are right now to actively pursuing your first deal.
          </p>
        </div>
        <div className="space-y-4">
          {ROADMAP.map(({ week, title, desc }, i) => (
            <div key={week} className="flex gap-4 rounded-xl border border-border bg-card p-5 shadow-card">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full gradient-gold text-sm font-bold text-white">
                {i + 1}
              </div>
              <div>
                <h3 className="font-serif font-semibold">{week}: {title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Final CTA ── */}
    <section className="gradient-gold py-20 text-white">
      <div className="mx-auto max-w-4xl px-4 text-center">
        <h2 className="font-serif text-3xl font-bold sm:text-4xl">Stop Waiting. Start Closing.</h2>
        <p className="mx-auto mt-4 max-w-2xl text-white/80">
          Education, mentorship, community, and accountability. Every tool you need to close your first deal in the next 12 months, backed by a full guarantee.
        </p>
        <Button asChild size="lg" className="mt-8 bg-white text-foreground shadow-lg hover:bg-white/90">
          <a href="https://join.firstdeal.com/main-calendar-app-form/" target="_blank" rel="noopener noreferrer">Apply to First Deal <ArrowRight className="ml-2 h-4 w-4" /></a>
        </Button>
        <p className="mt-4 text-sm text-white/60">Limited spots available. Application required.</p>
      </div>
    </section>

  </div>
);

export default Home;
